import React, { useEffect, useRef, useState } from "react";
import { LuX } from "react-icons/lu";
import vendorMenuController from "../controllers/vendorMenuController";
import { userService } from "../services/userService";
import { getProductImportResult } from "./productImportResult";
import { translateText, translateLabel, useTranslationSnapshot } from "./../translations/runtime";

const importMessageLabels = new Map([
  ["Could not import products. Please check the file and try again.", "txt_web_vendor_import_products_failed"],
  ["Products imported successfully.", "txt_web_vendor_products_imported_successfully"],
  ["Missing vendor session.", "txt_web_vendor_missing_vendor_session"],
]);

// Translate only application-owned wording; server details and identifiers are data.
const translateImportMessage = (message) => {
  const text = String(message ?? "");
  const row = /^Row (\d+): ([\s\S]*)$/.exec(text);
  if (row) return translateLabel("txt_web_vendor_import_row_error", "Row {value1}: {value2}", {
    value1: row[1], value2: row[2],
  });
  const id = importMessageLabels.get(text);
  return id ? translateLabel(id, text) : text;
};


export default function ProductImportModal({ onClose, onImported }) {
  useTranslationSnapshot();
  const [file, setFile] = useState(null);
  const [showValidation, setShowValidation] = useState(false);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const fileInputRef = useRef(null);
  const dialogRef = useRef(null);
  const resultsRef = useRef(null);
  const importInProgress = useRef(false);

  useEffect(() => {
    const previousFocus = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    dialogRef.current?.querySelector("button")?.focus();
    return () => {
      document.body.style.overflow = previousOverflow;
      previousFocus?.focus?.();
    };
  }, []);

  const handleKeyDown = (event) => {
    if (event.key === "Escape" && !importing) onClose();
    if (event.key !== "Tab") return;
    const controls = dialogRef.current.querySelectorAll("button:not(:disabled)");
    const first = controls[0];
    const last = controls[controls.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  const showResults = () => {
    setShowValidation(true);
    resultsRef.current?.scrollIntoView?.({ behavior: "smooth", block: "nearest" });
  };

  const importFile = async () => {
    if (!file || importInProgress.current) return;
    importInProgress.current = true;
    setImporting(true);
    setResult(null);
    setShowValidation(false);
    try {
      const companyId = userService.getUserData()?.Compid;
      const Authorization = userService.getJwt();
      if (!companyId || !Authorization) throw new Error("Missing vendor session.");
      const response = await vendorMenuController.importFile({ Authorization, companyId, file });
      const nextResult = getProductImportResult(response);
      setResult(nextResult);
      setShowValidation(nextResult.failed);
      if (!nextResult.failed) onImported?.();
    } catch (error) {
      setResult(getProductImportResult({
        isAxiosError: true,
        response: error?.response,
        message: error?.message,
      }));
      setShowValidation(true);
    } finally {
      importInProgress.current = false;
      setImporting(false);
    }
  };

  return (
    <div
      className="vendor-add-on-modal-backdrop vendor-product-import-backdrop"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !importing) onClose();
      }}
    >
      <div
        className="vendor-product-import-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="vendor-product-import-title"
        aria-busy={importing}
        ref={dialogRef}
        onKeyDown={handleKeyDown}
      >
        <div className="vendor-product-import-header">
          <h2 id="vendor-product-import-title">{translateLabel("txt_web_vendor_product_import", "Product Import")}</h2>
          <button type="button" aria-label={translateLabel("btn_web_vendor_close_product_import", "Close Product Import")} disabled={importing} onClick={onClose}>
            <LuX aria-hidden="true" />
          </button>
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept=".csv,.xlsx,.xls"
          aria-label={translateLabel("btn_web_vendor_upload_csv", "Upload CSV")}
          hidden
          disabled={importing}
          onChange={(event) => {
            const selectedFile = event.target.files?.[0];
            if (!selectedFile) return;
            setFile(selectedFile);
            setShowValidation(false);
            setResult(null);
          }}
        />
        <button
          className="vendor-product-import-upload"
          type="button"
          disabled={importing}
          onClick={() => fileInputRef.current?.click()}
        >
          {translateLabel("btn_web_vendor_upload_csv", "Upload CSV")}
        </button>

        {file && (
          <p className="vendor-product-import-file" title={file.name}>
            {translateLabel("txt_web_vendor_selected_import_file", "Selected file:")}{" "}<strong>{file.name}</strong>
          </p>
        )}

        <div className="vendor-product-import-actions">
          <button type="button" className="vendor-product-import-validate" disabled={importing || !result?.failed} onClick={showResults}> {translateLabel("txt_end_user_validation", "Validation")} </button>
          <button type="button" className="vendor-product-import-submit" disabled={!file || importing || (result && !result.failed)} onClick={importFile}>
            {importing ? translateLabel("txt_web_vendor_importing_products", "Importing...") : translateLabel("txt_web_vendor_import", "Import")}
          </button>
        </div>

        {result && !result.failed && (
          <p className="vendor-product-import-success" role="status">{translateImportMessage(result.message)}</p>
        )}

        {showValidation && result?.failed && (
          <>
            <section ref={resultsRef} className="vendor-product-import-results" aria-label={translateLabel("txt_web_vendor_import_validation_results", "Validation results")} role="alert">
              <dl className="vendor-product-import-summary">
                {result.summary.map(([label, value]) => (
                  <div key={label}>
                    <dt>{translateText(label)}</dt>
                    <dd>{value}</dd>
                  </div>
                ))}
              </dl>
              <h3>{translateLabel("txt_web_vendor_import_errors", "Errors")}</h3>
              <ul className="vendor-product-import-errors">
                {result.errors.map((error, index) => (
                  <li key={index}>{translateImportMessage(error)}</li>
                ))}
              </ul>
            </section>
            <div className="vendor-product-import-footer">
              <button type="button" className="vendor-product-import-validate" onClick={showResults}> {translateLabel("txt_end_user_validation", "Validation")} </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
